import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { RegistroContatoService } from "./api-registroContato.service";
import { RegistroContatoSignature } from "../../models/signature/RegistroContatoSignature";
import { HistoricoContatoSignature } from "../../models/signature/HistoricoContatoSignature";
import { FiltroRegistroContatoSignature } from "../../models/signature/FiltroRegistroContatoSignature";
import { ArquivoRegistroFeedbackDataService } from "../../../../status-negocio/shared/services/ArquivoController/arquivo-registro-feedback-data.service";

export interface SalvarHistoricoComAnexosResult {
  historicoOk: boolean;
  uploadOk: boolean;
  clientHistoryId?: string;
}

@Injectable({
  providedIn: 'root'
})

export class RegistroContatoDataService {

  constructor(
    private api: RegistroContatoService,
    private arquivoFeedbackService: ArquivoRegistroFeedbackDataService
  ) {
  }

  public GetRegistroContatoCliente(dealId: string): Observable<any> {
    return this.api.GetRegistroContatoCliente(dealId);
  }

  public UpdateRegistroContatoCliente(signature: RegistroContatoSignature): Observable<any> {
    return this.api.UpdateRegistroContatoCliente(signature);
  }

  public InsertHistoricoContato(signature: HistoricoContatoSignature): Observable<any> {
    return this.api.InsertHistoricoContato(signature);
  }

  public UpdateHistoricoContato(signature: HistoricoContatoSignature): Observable<any> {
    return this.api.UpdateHistoricoContato(signature);
  }

  public GetHistoricoContatoCliente(dealId: string): Observable<any> {
    return this.api.GetHistoricoContatoCliente(dealId);
  }

  public SalvarHistoricoComAnexos(
    signature: HistoricoContatoSignature,
    arquivos: File[] = []
  ): Observable<SalvarHistoricoComAnexosResult> {
    const arquivosValidos = (arquivos ?? []).filter(arquivo => !!arquivo);
    const salvar$ = signature.id
      ? this.UpdateHistoricoContato(signature)
      : this.InsertHistoricoContato(signature);

    return salvar$.pipe(
      switchMap(response => {
        const historicoOk = (response?.success ?? response?.Success) !== false;
        if (!historicoOk) {
          return of({ historicoOk: false, uploadOk: true });
        }

        if (!arquivosValidos.length) {
          const clientHistoryId = signature.id ? String(signature.id) : undefined;
          return of({ historicoOk: true, uploadOk: true, clientHistoryId });
        }

        const clientHistoryIdExistente = signature.id ? String(signature.id) : null;
        if (clientHistoryIdExistente) {
          return this.enviarAnexosHistorico(clientHistoryIdExistente, arquivosValidos, historicoOk);
        }

        return this.GetHistoricoContatoCliente(String(signature.clienteId)).pipe(
          switchMap(historicoResponse => {
            const clientHistoryId = this.extrairHistoricoId(historicoResponse);
            if (!clientHistoryId) {
              return of({ historicoOk: true, uploadOk: false });
            }
            return this.enviarAnexosHistorico(clientHistoryId, arquivosValidos, historicoOk);
          }),
          catchError(() => of({ historicoOk: true, uploadOk: false }))
        );
      })
    );
  }

  /** Garante registro em clientHistory (cria vazio se necessário) para vincular anexos. */
  public GarantirClientHistoryId(
    clienteId: string,
    resumo: string,
    historicoId?: string | number | null
  ): Observable<string | null> {
    if (historicoId != null && historicoId !== '') {
      return of(String(historicoId));
    }

    const signature = new HistoricoContatoSignature({
      clienteId,
      resumo: resumo ?? ''
    });

    return this.InsertHistoricoContato(signature).pipe(
      switchMap(() => this.GetHistoricoContatoCliente(String(clienteId))),
      map(response => this.extrairHistoricoId(response)),
      catchError(() => of(null))
    );
  }

  public EnviarAnexosHistoricoExecutivo(
    clientHistoryId: string,
    arquivos: File[]
  ): Observable<boolean> {
    const arquivosValidos = (arquivos ?? []).filter(arquivo => !!arquivo);
    if (!arquivosValidos.length) {
      return of(true);
    }

    return this.arquivoFeedbackService.incluirAnexosHistoricoExecutivo(clientHistoryId, arquivosValidos).pipe(
      map((uploadResponse: any) => (uploadResponse?.success ?? uploadResponse?.Success) !== false),
      catchError(() => of(false))
    );
  }

  private enviarAnexosHistorico(
    clientHistoryId: string,
    arquivos: File[],
    historicoOk: boolean
  ): Observable<SalvarHistoricoComAnexosResult> {
    return this.arquivoFeedbackService.incluirAnexosHistoricoExecutivo(clientHistoryId, arquivos).pipe(
      map((uploadResponse: any) => ({
        historicoOk,
        uploadOk: (uploadResponse?.success ?? uploadResponse?.Success) !== false,
        clientHistoryId
      })),
      catchError(() => of({ historicoOk, uploadOk: false, clientHistoryId }))
    );
  }

  private extrairHistoricoId(response: any): string | null {
    const success = response?.success ?? response?.Success;
    const data = response?.data ?? response?.Data;
    if (!success || !data) {
      return null;
    }
    const id = data.id ?? data.Id;
    return id ? String(id) : null;
  }

  public UpdateFeedbackContatoCliente(signature: RegistroContatoSignature): Observable<any> {
    return this.api.UpdateFeedbackContatoCliente(signature);
  }

  public UpdateRegistroContatoRetornoCliente(signature: RegistroContatoSignature): Observable<any> {
    return this.api.UpdateRegistroContatoRetornoCliente(signature);
  }

  public GetTodosRegistroContatoCliente(filtro: FiltroRegistroContatoSignature): Observable<any> {
    return this.api.GetTodosRegistroContatoCliente(filtro);
  }

  public GetTodosRegistrosContatoClienteSimplificados(filtro: FiltroRegistroContatoSignature): Observable<any> {
    return this.api.GetTodosRegistrosContatoClienteSimplificados(filtro);
  }
}
